import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Input } from '../../components/ui/input'
import CopyableId from '../../components/CopyableId'
import { Card, CardContent } from '../../components/ui/card'
import { ShieldCheckIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-md border ${
      active
        ? 'bg-psg-teal text-white border-psg-teal'
        : 'bg-white text-psg-teal border-psg-teal hover:bg-psg-teal-50'
    }`}
  >
    {children}
  </button>
)

function StatusBadge({ type, status }) {
  const map = {
    AML_PEP: {
      auto_approved: { label: 'Auto Approved', className: 'bg-green-100 text-green-800' },
      flagged_manual: { label: 'Flagged (Manual)', className: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
      on_hold: { label: 'On Hold', className: 'bg-gray-100 text-gray-800' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-800' }
    },
    DVS: {
      New: { label: 'New', className: 'bg-gray-100 text-gray-800' },
      Submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-800' },
      Verified: { label: 'Verified', className: 'bg-green-100 text-green-800' },
      Rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
      Resubmitted: { label: 'Resubmitted', className: 'bg-blue-100 text-blue-800' },
      'Attempts Exceeded': { label: 'Attempts Exceeded', className: 'bg-gray-100 text-gray-800' },
      Failed: { label: 'Failed', className: 'bg-red-100 text-red-800' }
    }
  }
  const m = map[type]?.[status] || { label: status, className: 'bg-gray-100 text-gray-800' }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.className}`}>{m.label}</span>
}

function AmlTable({ tasks, onDecide }) {
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [recvFrom, setRecvFrom] = useState('')
  const [recvTo, setRecvTo] = useState('')
  const [decFrom, setDecFrom] = useState('')
  const [decTo, setDecTo] = useState('')

  const inRange = (iso, from, to) => {
    if (!iso) return false
    const d = new Date(iso)
    if (from && d < new Date(from)) return false
    if (to) {
      const end = new Date(to)
      end.setHours(23,59,59,999)
      if (d > end) return false
    }
    return true
  }

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      const matchesQuery = t.masterProfileId.toLowerCase().includes(query.toLowerCase()) || t.status.toLowerCase().includes(query.toLowerCase())
      const matchesStatus = !statusFilter || t.status === statusFilter
      const matchesRecv = (!recvFrom && !recvTo) || inRange(t.receivedAt, recvFrom, recvTo)
      const matchesDec = (!decFrom && !decTo) || (t.decidedAt && inRange(t.decidedAt, decFrom, decTo))
      return matchesQuery && matchesStatus && matchesRecv && matchesDec
    })
  }, [tasks, query, statusFilter, recvFrom, recvTo, decFrom, decTo])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search Profile ID or status" value={query} onChange={e => setQuery(e.target.value)} className="w-64" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-10 border border-gray-300 rounded-md px-2 text-sm">
          <option value="">All statuses</option>
          <option value="auto_approved">Auto Approved</option>
          <option value="flagged_manual">Flagged (Manual)</option>
          <option value="approved">Approved</option>
          <option value="on_hold">On Hold</option>
          <option value="rejected">Rejected</option>
        </select>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Received</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={recvFrom} onChange={e => setRecvFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={recvTo} onChange={e => setRecvTo(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Decided</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={decFrom} onChange={e => setDecFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={decTo} onChange={e => setDecTo(e.target.value)} />
        </div>
      </div>
      <div className="overflow-x-auto border rounded-md bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Profile ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Decision Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map(task => (
              <tr key={task.id}>
                <td className="px-4 py-2 text-sm text-gray-900"><CopyableId value={task.masterProfileId} label={null} /></td>
                <td className="px-4 py-2 text-sm text-gray-600 capitalize">{task.trigger}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{task.riskScore}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{new Date(task.receivedAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{task.decidedAt ? new Date(task.decidedAt).toLocaleString() : '-'}</td>
                <td className="px-4 py-2 text-sm"><StatusBadge type="AML_PEP" status={task.status} /></td>
                <td className="px-4 py-2">
                  {task.status === 'flagged_manual' && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => onDecide(task.id, 'approved')}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => onDecide(task.id, 'on_hold')}>Hold</Button>
                      <Button size="sm" variant="destructive" onClick={() => onDecide(task.id, 'rejected')}>Reject</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function DvsTable({ tasks, onResubmit }) {
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [recvFrom, setRecvFrom] = useState('')
  const [recvTo, setRecvTo] = useState('')
  const [decFrom, setDecFrom] = useState('')
  const [decTo, setDecTo] = useState('')

  const inRange = (iso, from, to) => {
    if (!iso) return false
    const d = new Date(iso)
    if (from && d < new Date(from)) return false
    if (to) {
      const end = new Date(to)
      end.setHours(23,59,59,999)
      if (d > end) return false
    }
    return true
  }

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      const matchesQuery = t.masterProfileId.toLowerCase().includes(query.toLowerCase()) || t.status.toLowerCase().includes(query.toLowerCase())
      const matchesStatus = !statusFilter || t.status === statusFilter
      const matchesRecv = (!recvFrom && !recvTo) || inRange(t.receivedAt, recvFrom, recvTo)
      const matchesDec = (!decFrom && !decTo) || (t.decidedAt && inRange(t.decidedAt, decFrom, decTo))
      return matchesQuery && matchesStatus && matchesRecv && matchesDec
    })
  }, [tasks, query, statusFilter, recvFrom, recvTo, decFrom, decTo])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search Profile ID or status" value={query} onChange={e => setQuery(e.target.value)} className="w-64" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-10 border border-gray-300 rounded-md px-2 text-sm">
          <option value="">All statuses</option>
          <option value="New">New</option>
          <option value="Submitted">Submitted</option>
          <option value="Verified">Verified</option>
          <option value="Rejected">Rejected</option>
          <option value="Resubmitted">Resubmitted</option>
        </select>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Received</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={recvFrom} onChange={e => setRecvFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={recvTo} onChange={e => setRecvTo(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Decided</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={decFrom} onChange={e => setDecFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input type="date" className="h-10 border border-gray-300 rounded-md px-2 text-sm" value={decTo} onChange={e => setDecTo(e.target.value)} />
        </div>
      </div>
      <div className="overflow-x-auto border rounded-md bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Profile ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc Type</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Decision Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map(task => (
              <tr key={task.id}>
                <td className="px-4 py-2 text-sm text-gray-900"><CopyableId value={task.masterProfileId} label={null} /></td>
                <td className="px-4 py-2 text-sm text-gray-600 capitalize">{task.trigger}</td>
                <td className="px-4 py-2 text-sm text-gray-600 capitalize">{task.documentType?.replace('_', ' ')}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{task.attemptCount}/{task.maxAttempts}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{new Date(task.receivedAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{task.decidedAt ? new Date(task.decidedAt).toLocaleString() : '-'}</td>
                <td className="px-4 py-2 text-sm"><StatusBadge type="DVS" status={task.status} /></td>
                <td className="px-4 py-2">
                  <div className="flex justify-end">
                    {task.status === 'Rejected' && task.attemptCount < (task.maxAttempts || 3) ? (
                      <Button size="sm" onClick={() => onResubmit(task.id)}>Edit & Resubmit</Button>
                    ) : (
                      <Button size="sm" variant="ghost" disabled>—</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ProfileChecks({ initialTab = 'aml' }) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(initialTab === 'dvs' ? 'dvs' : 'aml')
  const [amlTasks, setAmlTasks] = useState([])
  const [dvsTasks, setDvsTasks] = useState([])

  useEffect(() => {
    const loadChecks = async () => {
      try {
        const [amlRes, dvsRes] = await Promise.all([
          authFetch(getLocalApiUrl('/api/profile-checks?type=aml')),
          authFetch(getLocalApiUrl('/api/profile-checks?type=dvs'))
        ])
        const amlData = await amlRes.json()
        const dvsData = await dvsRes.json()
        if (amlRes.ok && amlData.success) setAmlTasks(amlData.data || [])
        if (dvsRes.ok && dvsData.success) setDvsTasks(dvsData.data || [])
      } catch (err) {
        console.error('Failed to load profile checks:', err)
      }
    }
    loadChecks()
  }, [])

  const amlCounts = useMemo(() => ({
    total: amlTasks.length,
    flagged: amlTasks.filter(t => t.status === 'flagged_manual').length,
    autoApproved: amlTasks.filter(t => t.status === 'auto_approved').length,
    approved: amlTasks.filter(t => t.status === 'approved').length,
    rejected: amlTasks.filter(t => t.status === 'rejected').length,
  }), [amlTasks])

  const dvsCounts = useMemo(() => ({
    total: dvsTasks.length,
    rejected: dvsTasks.filter(t => t.status === 'Rejected').length,
    verified: dvsTasks.filter(t => t.status === 'Verified').length,
    submitted: dvsTasks.filter(t => ['New','Submitted','Resubmitted'].includes(t.status)).length,
    retries: dvsTasks.filter(t => t.status === 'Resubmitted').length,
  }), [dvsTasks])

  const decideAml = (id, decision) => {
    setAmlTasks(prev => prev.map(t => t.id === id ? { ...t, status: decision, decidedAt: new Date().toISOString() } : t))
  }

  const resubmitDvs = (id) => {
    setDvsTasks(prev => prev.map(t => {
      if (t.id !== id) return t
      const nextAttempts = (t.attemptCount || 1) + 1
      const exceeded = nextAttempts > (t.maxAttempts || 3)
      return {
        ...t,
        attemptCount: nextAttempts,
        status: exceeded ? 'Attempts Exceeded' : 'Resubmitted',
        decidedAt: exceeded ? new Date().toISOString() : null
      }
    }))
  }

  return (
    <div className="space-y-6">
      {/* Mini dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">AML/PEP Total</div>
            <div className="text-2xl font-semibold text-psg-teal">{amlCounts.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">AML Approved / Rejected</div>
            <div className="text-lg font-semibold"><span className="text-green-600 mr-2">{amlCounts.approved + amlCounts.autoApproved}</span><span className="text-red-600">{amlCounts.rejected}</span></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">DVS Submitted / Verified</div>
            <div className="text-lg font-semibold"><span className="text-psg-teal mr-2">{dvsCounts.submitted}</span><span className="text-green-600">{dvsCounts.verified}</span></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="text-xs text-gray-500">DVS Retries</div>
            <div className="text-2xl font-semibold text-amber-600">{dvsCounts.retries}</div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky segmented tabs with icons and counts */}
      <div className="sticky top-0 z-10">
        <div className="inline-flex rounded-md shadow-sm border border-psg-teal overflow-hidden bg-white">
          <button
            role="tab"
            aria-selected={activeTab === 'aml'}
            onClick={() => { setActiveTab('aml'); navigate('/profile-safe/checks/aml-pep') }}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${activeTab === 'aml' ? 'bg-psg-teal text-white' : 'bg-white text-psg-teal hover:bg-psg-teal-50'}`}
          >
            <ShieldCheckIcon className="h-4 w-4" />
            <span>AML/PEP</span>
            <span className={`ml-1 inline-flex items-center rounded-full px-2 text-xs font-semibold ${activeTab === 'aml' ? 'bg-white/20 text-white' : 'bg-psg-teal/10 text-psg-teal'}`}>
              {amlCounts.approved + amlCounts.autoApproved} / {amlCounts.rejected}
            </span>
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'dvs'}
            onClick={() => { setActiveTab('dvs'); navigate('/profile-safe/checks/dvs') }}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-l border-psg-teal transition-colors ${activeTab === 'dvs' ? 'bg-psg-teal text-white' : 'bg-white text-psg-teal hover:bg-psg-teal-50'}`}
          >
            <IdentificationIcon className="h-4 w-4" />
            <span>DVS</span>
            <span className={`ml-1 inline-flex items-center rounded-full px-2 text-xs font-semibold ${activeTab === 'dvs' ? 'bg-white/20 text-white' : 'bg-psg-teal/10 text-psg-teal'}`}>
              {dvsCounts.submitted} / {dvsCounts.verified} / {dvsCounts.retries}
            </span>
          </button>
        </div>
      </div>

      {/* Status chips */}
      <div className="flex flex-wrap items-center gap-3">
        {activeTab === 'aml' ? (
          <>
            <Badge className="bg-psg-teal text-white">Total: {amlCounts.total}</Badge>
            <Badge className="bg-yellow-100 text-yellow-800">Flagged: {amlCounts.flagged}</Badge>
            <Badge className="bg-green-100 text-green-800">Approved: {amlCounts.approved + amlCounts.autoApproved}</Badge>
            <Badge className="bg-red-100 text-red-800">Rejected: {amlCounts.rejected}</Badge>
          </>
        ) : (
          <>
            <Badge className="bg-psg-teal text-white">Submitted: {dvsCounts.submitted}</Badge>
            <Badge className="bg-green-100 text-green-800">Verified: {dvsCounts.verified}</Badge>
            <Badge className="bg-blue-100 text-blue-800">Retries: {dvsCounts.retries}</Badge>
          </>
        )}
      </div>

      {/* Tables */}
      <div>
        {activeTab === 'aml' ? (
          <AmlTable tasks={amlTasks} onDecide={decideAml} />
        ) : (
          <DvsTable tasks={dvsTasks} onResubmit={resubmitDvs} />
        )}
      </div>
    </div>
  )
}


